import { useEffect, useRef, useState } from "react";

import { getSharedTalents } from "../services/sharedTalentService";

const PAGE_SIZE = 12;

export function useSharedTalents() {
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    total: 0,
    nextOffset: 0,
    hasMore: false,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const requestRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    const loadFirstPage = async () => {
      requestRef.current = true;
      setLoading(true);
      setError("");
      setTalents([]);

      try {
        const response = await getSharedTalents(
          PAGE_SIZE,
          0,
          controller.signal,
        );

        if (controller.signal.aborted) return;

        if (response?.data?.success === false) {
          throw new Error(response.data.message || "Unable to load talents.");
        }

        setTalents(response?.data?.talents || []);
        setPagination(
          response?.data?.pagination || {
            total: 0,
            nextOffset: null,
            hasMore: false,
          },
        );
      } catch (err) {
        if (controller.signal.aborted) return;

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load talents.",
        );
      } finally {
        if (!controller.signal.aborted) {
          requestRef.current = false;
          setLoading(false);
        }
      }
    };

    loadFirstPage();

    return () => {
      controller.abort();
      requestRef.current = false;
    };
  }, [refreshKey]);

  const loadMore = async () => {
    if (
      requestRef.current ||
      loading ||
      loadingMore ||
      !pagination.hasMore ||
      pagination.nextOffset === null
    ) {
      return;
    }

    requestRef.current = true;
    setLoadingMore(true);
    setError("");

    try {
      const response = await getSharedTalents(PAGE_SIZE, pagination.nextOffset);

      if (response?.data?.success === false) {
        throw new Error(response.data.message || "Unable to load talents.");
      }

      const next = response?.data?.talents || [];

      setTalents((previous) => {
        const existing = new Set(previous.map((item) => item.talentId));

        return [
          ...previous,
          ...next.filter((item) => !existing.has(item.talentId)),
        ];
      });

      setPagination(
        response?.data?.pagination || {
          total: 0,
          nextOffset: null,
          hasMore: false,
        },
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load talents.",
      );
    } finally {
      requestRef.current = false;
      setLoadingMore(false);
    }
  };

  return {
    talents,
    loading,
    loadingMore,
    error,
    total: pagination.total,
    hasMore: pagination.hasMore,
    loadMore,
    refresh: () => {
      if (requestRef.current) return;
      setRefreshKey((value) => value + 1);
    },
  };
}
